import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";

import { api } from "@/api";
import { venueLabel } from "@/features/racing/venue";

import {
  ScheduleEmpty,
  ScheduleError,
  ScheduleSkeleton,
} from "./ScheduleStates";

export function RaceDetails({
  raceId,
  official,
  racecardHash,
}: {
  raceId: string;
  official: boolean;
  racecardHash?: string;
}) {
  const card = useQuery({
    queryKey: ["race", raceId, "card"],
    queryFn: () => api.racecard(raceId),
    staleTime: Infinity,
  });
  const playback = useQuery({
    queryKey: ["race", raceId, "playback"],
    queryFn: () => api.playback(raceId),
    enabled: official,
    // A Home viewer may have cached this race while it was still running.
    staleTime: 0,
  });
  const response = card.data;
  const identityMatches =
    response?.racecard.raceId === raceId &&
    (!racecardHash || response.racecardHash === racecardHash);
  const result = playback.data;
  const officialResult =
    official &&
    result?.raceId === raceId &&
    result.status === "official" &&
    result.racecardHash === response?.racecardHash;
  const runners = response?.racecard.runners ?? [];
  const field = officialResult
    ? result.order.flatMap((gate, index) => {
        const runner = runners.find((entry) => entry.gate === gate);
        return runner ? [{ runner, position: index + 1 }] : [];
      })
    : [...runners]
        .sort((a, b) => a.gate - b.gate)
        .map((runner) => ({ runner, position: null }));

  return (
    <div className="schedule-details">
      <div className="schedule-details__actions">
        {!official ? (
          <Link
            className="schedule-button schedule-button--primary"
            href={`/app/?market=${raceId}&tab=bet`}
          >
            View markets
          </Link>
        ) : null}
        <Link className="schedule-button" href={`/app/?watch=${raceId}`}>
          {official ? "Replay" : "Watch"}
        </Link>
        <Link className="schedule-button" href={`/app/verify/${raceId}`}>
          Verify
        </Link>
      </div>
      {card.isPending ? (
        <ScheduleSkeleton field />
      ) : card.isError || !identityMatches ? (
        <ScheduleError
          title="Published field could not be loaded"
          retry={() => void card.refetch()}
        />
      ) : (
        <>
          <div className="schedule-details__heading">
            <strong>{official ? "Official finish" : "Published field"}</strong>
            <span>{venueLabel(response.racecard)}</span>
          </div>
          {official &&
          (playback.isPending || (!officialResult && playback.isFetching)) ? (
            <ScheduleSkeleton field />
          ) : official && (playback.isError || !officialResult) ? (
            <ScheduleError
              title="Official result details could not be loaded"
              retry={() => void playback.refetch()}
            />
          ) : field.length ? (
            <div
              className="schedule-field-scroll"
              tabIndex={0}
              role="region"
              aria-label={
                official ? "Official finish details" : "Published field details"
              }
            >
              <table className="schedule-field">
                <thead>
                  <tr>
                    {official ? <th scope="col">Place</th> : null}
                    <th scope="col">Gate</th>
                    <th scope="col">Horse</th>
                    <th scope="col">
                      {official ? "Time" : "Published weight"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {field.map(({ runner, position }) => {
                    const finish = officialResult
                      ? result.finishTimesMs[runner.gate]
                      : undefined;
                    return (
                      <tr key={runner.gate}>
                        {official ? <td>{position}</td> : null}
                        <td>
                          <span className={`gate gate--${runner.gate + 1}`}>
                            {runner.gate + 1}
                          </span>
                        </td>
                        <th scope="row">{runner.name}</th>
                        <td>
                          {official
                            ? Number.isFinite(finish)
                              ? `${(finish! / 1000).toFixed(2)}s`
                              : "Not published"
                            : runner.weight}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <ScheduleEmpty
              title="No published runners"
              detail="Field details will appear when they are available."
            />
          )}
        </>
      )}
    </div>
  );
}
